import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api, { money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Loader2, MapPin, TrendingUp, Clock, Users, Briefcase, Zap, ShieldCheck, ArrowLeft,
  Smartphone, CreditCard, Building2, Wallet, CheckCircle2, Send,
} from "lucide-react";

const PAYMENTS = [
  { id: "mpesa", label: "M-Pesa", icon: Smartphone },
  { id: "orange", label: "Orange Money", icon: Smartphone },
  { id: "airtel", label: "Airtel Money", icon: Smartphone },
  { id: "card", label: "Carte Visa / Mastercard", icon: CreditCard },
  { id: "transfer", label: "Virement bancaire CADECO", icon: Building2 },
  { id: "cash", label: "Espèces en agence", icon: Wallet },
];

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("amount");
  const [amount, setAmount] = useState(50);
  const [pay, setPay] = useState("mpesa");
  const [invId, setInvId] = useState(null);
  const [otpDemo, setOtpDemo] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [contract, setContract] = useState("");
  const [largeThreshold, setLargeThreshold] = useState(1000);
  const [largeAck, setLargeAck] = useState(false);
  const [forum, setForum] = useState([]);
  const [msg, setMsg] = useState("");

  const load = useCallback(() => api.get(`/projects/${id}`).then(({ data }) => setP(data)), [id]);
  const loadForum = useCallback(() => api.get(`/projects/${id}/forum`).then(({ data }) => setForum(data)).catch(() => {}), [id]);
  useEffect(() => { load(); loadForum(); }, [load, loadForum]);

  if (!p) return <Layout><div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-congo-blue" /></div></Layout>;

  const remaining = p.funding_goal - p.amount_collected;

  const startInvest = async () => {
    if (!user) { navigate("/auth?mode=register"); return; }
    if (user.kyc_status !== "approved") {
      toast.error("Vérification KYC requise avant d'investir.");
      navigate("/kyc");
      return;
    }
    try {
      const { data: st } = await api.get("/eval/status");
      if (!st.can_invest) {
        toast.info("Complétez votre évaluation investisseur avant d'investir.");
        navigate("/evaluation");
        return;
      }
      setLargeThreshold(st.large_invest_threshold || 1000);
    } catch (e) { /* le backend applique aussi le contrôle à la soumission */ }
    setStep("amount"); setAmount(50); setInvId(null); setContract(""); setLargeAck(false); setOpen(true);
  };

  const createInv = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/investments", { project_id: id, amount: Number(amount), payment_method: pay, large_ack: largeAck });
      setInvId(data.investment_id); setOtpDemo(data.otp_demo); setStep("otp");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const confirmInv = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/investments/${invId}/confirm`, { otp });
      setContract(data.contract_id); setStep("done"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Code invalide"); }
    finally { setBusy(false); }
  };

  const sendMsg = async () => {
    if (!user) { navigate("/auth"); return; }
    if (!msg.trim()) return;
    await api.post(`/projects/${id}/forum`, { message: msg });
    setMsg(""); loadForum();
  };

  return (
    <Layout>
      <div className="relative h-64 md:h-80 bg-slate-900">
        <img src={p.image_url || "https://images.unsplash.com/photo-1729006122953-e3cafd3dd875?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwzfHxLaW5zaGFzYXxlbnwwfHx8fDE3ODYzODU1NjJ8MA&ixlib=rb-4.1.0&q=85"} alt={p.title} className="h-full w-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 max-w-7xl mx-auto px-4 pb-6">
          <button onClick={() => navigate("/projets")} className="text-white/80 flex items-center gap-1 text-sm mb-3 hover:text-white" data-testid="back-btn"><ArrowLeft className="h-4 w-4" /> Retour</button>
          <Badge className="bg-congo-gold text-slate-900 border-0 mb-2">{p.sector}</Badge>
          <h1 className="font-head text-3xl md:text-4xl font-bold text-white">{p.title}</h1>
          <p className="text-white/80 flex items-center gap-1 mt-1"><MapPin className="h-4 w-4" />{p.location}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <Tabs defaultValue="apercu">
            <TabsList className="bg-slate-100">
              <TabsTrigger value="apercu" data-testid="tab-overview">Aperçu</TabsTrigger>
              <TabsTrigger value="impact" data-testid="tab-impact">Impact</TabsTrigger>
              <TabsTrigger value="forum" data-testid="tab-forum">Forum</TabsTrigger>
            </TabsList>
            <TabsContent value="apercu" className="pt-6 space-y-4">
              <h2 className="font-head text-xl font-semibold">À propos du projet</h2>
              <p className="text-slate-600 leading-relaxed">{p.description}</p>
              {p.story && <blockquote className="border-l-4 border-congo-gold pl-4 italic text-slate-700">{p.story}</blockquote>}
              {p.financing_type && (
                <div className="flex items-center gap-3 bg-congo-bluelight/50 border border-congo-blue/20 rounded-xl p-4" data-testid="financing-type">
                  <Building2 className="h-6 w-6 text-congo-blue shrink-0" />
                  <div><p className="text-xs uppercase tracking-wider font-bold text-congo-blue">Nature du montage</p><p className="font-semibold text-slate-900">{p.financing_type}</p></div>
                </div>
              )}
              {p.partners && p.partners.length > 0 && (
                <div data-testid="project-partners">
                  <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Partenaires du consortium</p>
                  <div className="flex flex-wrap gap-2">
                    {p.partners.map((pa) => <Badge key={pa} className="bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200">{pa}</Badge>)}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4">
                {[
                  { icon: TrendingUp, l: "Rendement", v: `${p.interest_rate}% / an` },
                  { icon: Clock, l: "Durée", v: `${p.duration_months} mois` },
                  { icon: Users, l: "Investisseurs", v: p.investors_count },
                  { icon: ShieldCheck, l: "Risque", v: `${p.risk_level}/5` },
                ].map((s) => (
                  <div key={s.l} className="bg-white border border-slate-200 rounded-xl p-4">
                    <s.icon className="h-5 w-5 text-congo-blue" />
                    <p className="text-xs text-slate-500 mt-2">{s.l}</p>
                    <p className="font-head font-bold text-slate-900">{s.v}</p>
                  </div>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="impact" className="pt-6">
              <h2 className="font-head text-xl font-semibold mb-4">Impact social & national</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-xl p-5">
                  <Briefcase className="h-6 w-6 text-green-600" />
                  <p className="font-head text-2xl font-bold text-slate-900 mt-2">{p.jobs_impact}</p>
                  <p className="text-sm text-slate-600">emplois directs créés</p>
                </div>
                {p.energy_impact && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                    <Zap className="h-6 w-6 text-amber-600" />
                    <p className="font-head text-2xl font-bold text-slate-900 mt-2">{p.energy_impact}</p>
                    <p className="text-sm text-slate-600">capacité énergétique</p>
                  </div>
                )}
              </div>
            </TabsContent>
            <TabsContent value="forum" className="pt-6">
              <h2 className="font-head text-xl font-semibold mb-4">Forum du projet</h2>
              <div className="space-y-3 mb-4" data-testid="forum-list">
                {forum.length === 0 && <p className="text-slate-500 text-sm">Aucun message. Soyez le premier à poser une question.</p>}
                {forum.map((m) => (
                  <div key={m.id} className={`rounded-xl p-3 ${m.is_admin ? "bg-congo-bluelight border border-congo-blue/20" : "bg-slate-50 border border-slate-200"}`}>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm text-slate-900">{m.user_name}</span>
                      {m.is_admin && <Badge className="bg-congo-blue text-white border-0 text-[10px]">Officiel</Badge>}
                    </div>
                    <p className="text-sm text-slate-700 mt-1">{m.message}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Votre message…" data-testid="forum-input" className="h-11 rounded-full" />
                <Button onClick={sendMsg} data-testid="forum-send" className="rounded-full bg-congo-blue h-11 w-11 p-0"><Send className="h-4 w-4" /></Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar invest */}
        <div>
          <div className="bg-white border border-slate-200 rounded-2xl p-6 sticky top-20">
            <div className="flex justify-between items-baseline">
              <span className="font-head text-2xl font-bold text-slate-900">{money(p.amount_collected)}</span>
              <span className="text-sm text-slate-500">{p.progress_pct}%</span>
            </div>
            <Progress value={p.progress_pct} className="h-2.5 mt-2" />
            <p className="text-sm text-slate-500 mt-2">sur un objectif de <span className="font-semibold text-slate-700">{money(p.funding_goal)}</span></p>
            <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
              <div className="bg-slate-50 rounded-lg p-3"><p className="text-slate-500 text-xs">Rendement</p><p className="font-bold text-congo-blue">{p.interest_rate}%/an</p></div>
              <div className="bg-slate-50 rounded-lg p-3"><p className="text-slate-500 text-xs">Restant</p><p className="font-bold text-slate-900">{money(remaining)}</p></div>
            </div>
            {p.financing_type && <div className="mt-3 bg-slate-50 rounded-lg p-3 text-sm"><p className="text-slate-500 text-xs">Nature du montage</p><p className="font-semibold text-slate-900">{p.financing_type}</p></div>}
            <Button onClick={startInvest} disabled={p.status !== "active" || remaining <= 0} data-testid="invest-btn"
              className="w-full mt-5 h-12 rounded-full bg-congo-gold text-slate-900 hover:bg-congo-golddark font-semibold text-base">
              {p.status !== "active" ? "Financement clôturé" : "Investir maintenant"}
            </Button>
            <p className="text-xs text-slate-400 text-center mt-3 flex items-center justify-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Fonds sécurisés par CADECO</p>
          </div>
        </div>
      </div>

      {/* Invest dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="invest-dialog">
          {step === "amount" && (
            <>
              <DialogHeader><DialogTitle className="font-head">Investir dans ce projet</DialogTitle>
                <DialogDescription>Montant minimum 50$, par multiples de 50$.</DialogDescription></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Montant (USD)</Label>
                  <Input type="number" min={50} step={50} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="amount-input" className="h-12 rounded-lg mt-1 text-lg font-semibold" />
                  <div className="flex gap-2 mt-2">
                    {[50, 100, 250, 500].map((a) => <button key={a} onClick={() => setAmount(a)} className="px-3 py-1.5 rounded-full text-sm bg-slate-100 hover:bg-congo-bluelight" data-testid={`quick-${a}`}>{a}$</button>)}
                  </div>
                </div>
                <div>
                  <Label>Méthode de paiement</Label>
                  <RadioGroup value={pay} onValueChange={setPay} className="mt-2 space-y-1">
                    {PAYMENTS.map((m) => (
                      <label key={m.id} className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer ${pay === m.id ? "border-congo-blue bg-congo-bluelight" : "border-slate-200"}`} data-testid={`pay-${m.id}`}>
                        <RadioGroupItem value={m.id} /><m.icon className="h-4 w-4 text-slate-600" /><span className="text-sm">{m.label}</span>
                      </label>
                    ))}
                  </RadioGroup>
                </div>
                {Number(amount) > largeThreshold && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="large-invest-warning">
                    <p className="text-sm text-amber-800 font-medium flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Investissement important</p>
                    <p className="text-xs text-amber-700 mt-1">Ce montant dépasse {money(largeThreshold)} (ou 5 % de votre patrimoine net). Un investissement important accroît votre exposition et le risque de perte en capital.</p>
                    <label className="flex items-start gap-2 mt-2 cursor-pointer" data-testid="large-invest-ack">
                      <Checkbox checked={largeAck} onCheckedChange={(v) => setLargeAck(!!v)} className="mt-0.5" />
                      <span className="text-xs text-amber-800">Je comprends et j'accepte le risque renforcé lié à ce montant.</span>
                    </label>
                  </div>
                )}
                <div className="bg-slate-50 rounded-lg p-3 text-sm flex justify-between"><span className="text-slate-500">Rendement estimé ({p.duration_months} mois)</span><span className="font-bold text-green-600">+{money(amount * (p.interest_rate / 100) * (p.duration_months / 12))}</span></div>
                <Button onClick={createInv} disabled={busy || (Number(amount) > largeThreshold && !largeAck)} data-testid="continue-payment-btn" className="w-full h-12 rounded-full bg-congo-blue">
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : `Payer ${money(amount)}`}
                </Button>
              </div>
            </>
          )}
          {step === "otp" && (
            <>
              <DialogHeader><DialogTitle className="font-head">Confirmation (double authentification)</DialogTitle>
                <DialogDescription>Saisissez le code envoyé par SMS/email.</DialogDescription></DialogHeader>
              <div className="space-y-4">
                <div className="bg-congo-goldlight border border-congo-gold/40 rounded-lg p-3 text-sm text-slate-700">
                  Mode démo — votre code est : <span className="font-bold font-head text-lg">{otpDemo}</span>
                </div>
                <Input value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="Code à 6 chiffres" data-testid="otp-input" className="h-12 rounded-lg text-center text-xl tracking-widest" />
                <Button onClick={confirmInv} disabled={busy} data-testid="confirm-otp-btn" className="w-full h-12 rounded-full bg-congo-blue">
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Confirmer l'investissement"}
                </Button>
              </div>
            </>
          )}
          {step === "done" && (
            <div className="text-center py-4" data-testid="invest-success">
              <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto" />
              <h3 className="font-head text-xl font-bold mt-4">Investissement confirmé !</h3>
              <p className="text-slate-600 mt-2">Votre contrat <span className="font-semibold">{contract}</span> a été généré.</p>
              <div className="flex gap-2 mt-6">
                <Button variant="outline" className="flex-1 rounded-full" onClick={() => setOpen(false)}>Fermer</Button>
                <Button className="flex-1 rounded-full bg-congo-blue" onClick={() => navigate("/tableau-de-bord")} data-testid="goto-dashboard-btn">Mon portefeuille</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
